import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2, UserPlus, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid' | 'accepted' | 'error'>('loading');
  const [invitation, setInvitation] = useState<any>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus('invalid');
        return;
      }

      try {
        const { data: inv, error } = await supabase
          .from('clinician_invitations')
          .select('*')
          .eq('invitation_token', token)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString())
          .single();

        if (error || !inv) {
          setStatus('invalid');
          return;
        }

        setInvitation(inv);
        setStatus('valid');
      } catch (err) {
        console.error('Error validating invitation:', err);
        setStatus('error');
      }
    };

    validateToken();
  }, [token]);

  const handleAccept = async () => {
    if (!token || !invitation) return;
    
    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // Store token and redirect to auth
        sessionStorage.setItem('pending_invitation_token', token);
        navigate('/auth?redirect=/accept-invitation&token=' + token);
        return;
      }

      // Call the edge function to accept
      const { data, error } = await supabase.functions.invoke('accept-clinician-invitation', {
        body: { token }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setStatus('accepted');
      toast({
        title: 'Connection established',
        description: 'You are now connected with your therapist.',
      });

      // Redirect based on invitation type
      setTimeout(() => {
        if (invitation.invitation_type === 'clinician_to_client') {
          navigate('/account?tab=integrations');
        } else {
          navigate('/clinician-portal');
        }
      }, 2000);
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      toast({
        title: 'Error accepting invitation',
        description: err.message || 'Please try again later.',
        variant: 'destructive'
      });
      setStatus('error');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!token || !invitation) return;
    
    setProcessing(true);
    try {
      // Update invitation status directly
      const { error } = await supabase
        .from('clinician_invitations')
        .update({ 
          status: 'declined',
          responded_at: new Date().toISOString()
        })
        .eq('invitation_token', token);

      if (error) throw error;

      toast({
        title: 'Invitation declined',
        description: 'The invitation has been declined.',
      });
      
      navigate('/');
    } catch (err: any) {
      console.error('Error declining invitation:', err);
      toast({
        title: 'Error',
        description: err.message || 'Please try again later.',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-border/50">
          <CardHeader className="text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
                <CardTitle>Validating Invitation</CardTitle>
                <CardDescription>Please wait while we verify your invitation...</CardDescription>
              </>
            )}
            
            {status === 'valid' && (
              <>
                <UserPlus className="w-12 h-12 text-primary mx-auto mb-4" />
                <CardTitle>Therapist Connection Invitation</CardTitle>
                <CardDescription>
                  You've been invited to connect with your therapist on Therappi
                </CardDescription>
              </>
            )}
            
            {status === 'accepted' && (
              <>
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <CardTitle>Successfully Connected!</CardTitle>
                <CardDescription>
                  You're now connected with your therapist. Redirecting...
                </CardDescription>
              </>
            )}
            
            {(status === 'invalid' || status === 'error') && (
              <>
                <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                <CardTitle>Invalid Invitation</CardTitle>
                <CardDescription>
                  This invitation link is invalid, has expired, or has already been used.
                </CardDescription>
              </>
            )}
          </CardHeader>
          
          <CardContent>
            {status === 'valid' && invitation && (
              <div className="space-y-6">
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {invitation.invitation_type === 'clinician_to_client' 
                      ? 'Invitation from your therapist' 
                      : 'Connection request from'}
                  </p>
                  <p className="font-medium text-foreground">
                    {invitation.sender_type === 'clinician' ? 'Your Therapist' : invitation.recipient_email}
                  </p>
                  {invitation.message && (
                    <>
                      <p className="text-sm text-muted-foreground mt-3">Personal message:</p>
                      <p className="text-sm text-foreground italic">"{invitation.message}"</p>
                    </>
                  )}
                </div>

                <div className="bg-primary/5 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-primary">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">Your Privacy is Protected</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    By accepting, you can choose exactly what information to share with your therapist. 
                    You control your data and can revoke access at any time.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleDecline}
                    disabled={processing}
                  >
                    Decline
                  </Button>
                  <Button
                    className="flex-1 bg-primary hover:bg-primary/90"
                    onClick={handleAccept}
                    disabled={processing}
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Accept & Connect
                  </Button>
                </div>
              </div>
            )}

            {(status === 'invalid' || status === 'error') && (
              <Button
                className="w-full"
                onClick={() => navigate('/')}
              >
                Return Home
              </Button>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AcceptInvitation;
